import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { isAppLocale, messageFor, type AppLocale, type MessageKey, type PluralMessageKey } from './catalog';

const STORAGE_KEY = 'frozen-save.interface-locale';
type MessageParameters = Readonly<Record<string, string | number>>;

const interpolate = (message: string, parameters?: MessageParameters): string =>
  message.replace(/\{([A-Za-z0-9_]+)\}/g, (placeholder, key: string) =>
    parameters?.[key] == null ? placeholder : String(parameters[key]),
  );

interface I18nValue {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
  t: (key: MessageKey, parameters?: MessageParameters) => string;
  tp: (key: PluralMessageKey, count: number, parameters?: MessageParameters) => string;
  formatDate: (value: Date, options?: Intl.DateTimeFormatOptions) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

const storedLocale = (): AppLocale => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return isAppLocale(value) ? value : 'fr-FR';
  } catch {
    return 'fr-FR';
  }
};

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const [locale, setLocaleState] = useState<AppLocale>(storedLocale);
  useEffect(() => {
    document.documentElement.lang = locale;
    document.title = messageFor(locale, 'meta.title');
    document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute(
      'content',
      messageFor(locale, 'meta.description'),
    );
    document.querySelector<HTMLLinkElement>('link[rel="manifest"]')?.setAttribute(
      'href',
      locale === 'en' ? '/manifest.en.webmanifest' : '/manifest.webmanifest',
    );
    try {
      window.localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // La préférence de langue est non critique ; l’inventaire reste dans IndexedDB.
    }
  }, [locale]);

  const value = useMemo<I18nValue>(() => {
    const translate = (key: MessageKey, parameters?: MessageParameters) =>
      interpolate(messageFor(locale, key), parameters);
    return ({
    locale,
    setLocale: setLocaleState,
    t: translate,
    tp: (key, count, parameters) => {
      const pluralLocale = locale === 'en-XA' ? 'fr-FR' : locale;
      const suffix = new Intl.PluralRules(pluralLocale).select(count) === 'one' ? '.one' : '.other';
      return translate(`${key}${suffix}` as MessageKey, { count, ...parameters });
    },
    formatDate: (date, options) => new Intl.DateTimeFormat(locale, options).format(date),
    formatNumber: (number, options) => new Intl.NumberFormat(locale, options).format(number),
  });
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = (): I18nValue => {
  const value = useContext(I18nContext);
  if (value == null) throw new Error('I18nProvider manquant.');
  return value;
};
